import classNames from "classnames";
import { FieldState } from "formstate";
import { observer } from "mobx-react";
import { ControllerStateAndHelpers } from "downshift";
import React, { useEffect } from "react";
import { intl } from "../../i18n/TypedIntl";
import { Country, countryService } from "../../services/countries";
import { Flag } from "../flag/flag";
import { AgentInput } from "../form/agentInput";
import { AutoComplete } from "../form/autoComplete/autoComplete";
import styles from "./phoneNumberInput.css";
import { AsYouType } from "libphonenumber-js";

/** ========================================================
 * BEGIN Auxiliary PhoneRegionInput component implementation
 =========================================================== */

interface PrefixGridProps {
  item: Country;
}
const PrefixGrid = ({ item: { code, name, iso } }: PrefixGridProps) => (
  <div className={styles["country-row"]}>
    <span className={styles["country-code"]}>+{code}</span>
    <Flag country={iso} />
    <span className={styles["country-name"]}>{name}</span>
  </div>
);

interface PhoneRegionProps {
  field: FieldState<string>;
  onChange?: (value: Country, stateAndHelpers: ControllerStateAndHelpers<Country>) => void;
  placeholder?: string;
  emptyPlaceholder: string;
  className?: string;
  menuClassName?: string;
}

@observer
export class PhoneRegionInput extends React.Component<PhoneRegionProps> {
  itemsProvider = (helpers: ControllerStateAndHelpers<Country>): Country[] => {
    const { inputValue } = helpers;
    return inputValue ? countryService.find(inputValue) : countryService.countries;
  };

  render() {
    return (
      <AutoComplete<Country>
        GridItem={PrefixGrid}
        itemsProvider={this.itemsProvider}
        enableReset
        itemToString={(item: Country | null) => item?.iso || ""}
        menuClassName={classNames(styles["prefix-menu"], this.props.menuClassName)}
        onInputValueChange={(value) => {
          this.props.field.onChange(value);
        }}
        inputValue={this.props.field.value}
        {...this.props}
      />
    );
  }
}

/** ==================================
 * BEGIN Main component implementation
 ===================================== */

interface Props {
  phoneNumber: FieldState<string | "">;
  phoneRegion: FieldState<string>;
  inputRef?: (el?: HTMLInputElement) => void;
  className?: string;
  id?: string;
}

/**
 * Phone number input component.
 * Displays two inputs: region selection and text input for the national number with prefix.
 * Does not perform any validation except for restricting entry in both fields.
 */
export const PhoneNumberInput = observer(function (props: Props) {
  const { className, phoneRegion, phoneNumber, id } = props;

  function reformatNumber() {
    const asYouType = new AsYouType();
    phoneNumber.value = asYouType.input(phoneNumber.value);
    if (!phoneRegion.value) {
      phoneRegion.value = asYouType?.getCountry() || "";
    }
  }

  useEffect(() => reformatNumber(), []);

  return (
    <div className={classNames(styles.container, className)} id={id}>
      <PhoneRegionInput
        field={phoneRegion}
        placeholder={intl.formatMessage("profile.edit.placeholder.code")}
        emptyPlaceholder={intl.formatMessage("profile.edit.noMatchingCountries")}
        onChange={(country) => {
          phoneNumber.value = `+${country.code}`;
        }}
      />
      <AgentInput
        field={phoneNumber}
        placeholder={intl.formatMessage("profile.edit.placeholder.phone")}
        onChange={() => {
          reformatNumber();
        }}
      />
    </div>
  );
});
